import { isEqual } from "es-toolkit";
import {
  type Schedule,
  type Rule,
  type WeeklyPatternRule,
  type RotatingPatternRule,
  type AbsoluteRangeRule,
  type LocalisedDateTime,
  dateTimeRecordToLocalisedDateTime,
  DateTimeRecord,
  DayOfTheWeek,
  timeRecordToMinutesFromMidnight,
  makeTimeRecord,
  dateTimeRecordToMinutesFromMidnight,
  dateTimeToDayOfWeek,
  LocalisedDate,
  dateRecordToLocalisedDate,
  makeDateRecord,
  DateRecord,
  EffectiveTimeRanges,
  dateTimeRecordToDateRecord,
  TimeRange,
  dateRecordToStartOfDay,
  dateRecordToEndOfDay,
  makeTimeRange,
  RequiredEffectivePeriod,
  minutesFromMidnightToTimeRecord,
  MINUTES_FROM_MIDNIGHT_END_OF_DAY,
  OptionalEffectivePeriod,
  AbsoluteRangeEffectivePeriod,
  dateRecordToDateTimeRecord,
} from "../types";
import { RuleFunctionError } from "./errors";

interface PeriodsAppliedOnDate {
  date: DateRecord;
  periods: TimeRange[];
}

export interface RuleFunction {
  appliesAt(datetime: DateTimeRecord): boolean;
  periodsAppliedOn(date: DateRecord): PeriodsAppliedOnDate;
}

export interface TimeRangeInMinutes {
  start: number;
  end: number;
}

interface _ProcessedEffectiveTimeRanges {
  isTimeRangeAllDay: boolean;
  minuteRanges: TimeRangeInMinutes[];
}
function _processTimeRanges(timeRanges: EffectiveTimeRanges): _ProcessedEffectiveTimeRanges {
  if (timeRanges[0].kind === "all_day") {
    return {
      isTimeRangeAllDay: true,
      minuteRanges: [],
    };
  }

  const processed: _ProcessedEffectiveTimeRanges = {
    isTimeRangeAllDay: false,
    minuteRanges: [],
  };
  for (const timeRange of timeRanges) {
    const kind = timeRange.kind;
    switch (kind) {
      case "time_range": {
        const isTimeRangeAllDay =
          isEqual(timeRange.start, makeTimeRecord(0, 0)) && isEqual(timeRange.end, makeTimeRecord(24, 0));
        if (isTimeRangeAllDay) {
          processed.isTimeRangeAllDay = true;
        }
        const startMinutes = timeRecordToMinutesFromMidnight(timeRange.start);
        const endMinutes = timeRecordToMinutesFromMidnight(timeRange.end);
        // If there is no range, we can no-op
        if (startMinutes === endMinutes) {
          break;
        }
        // Does the time range cross the day boundary?
        // Example: 6pm-6am. In the context of a pattern of days, we equate that
        // to 00:00-06:00 & 18:00-24:00 on the same day.
        if (startMinutes > endMinutes) {
          processed.minuteRanges.push(
            {
              start: 0,
              end: endMinutes,
            },
            {
              start: startMinutes,
              end: timeRecordToMinutesFromMidnight(makeTimeRecord(24, 0)),
            },
          );
          break;
        }
        processed.minuteRanges.push({
          start: startMinutes,
          end: endMinutes,
        });
        break;
      }
      case "all_day": {
        // This case should be handled above.
        throw new RuleFunctionError('Unhandled kind "all_day"');
      }
      default: {
        const _exhaustive: never = kind;
        throw new RuleFunctionError(`Unknown kind ${JSON.stringify(_exhaustive)}`);
      }
    }
  }
  return processed;
}

/**
 * Resolves the intersection of time periods on a day
 */
function _periodIntersection(periods1: TimeRangeInMinutes[], periods2: TimeRangeInMinutes[]): TimeRange[] {
  const intersectingPeriods: TimeRange[] = [];
  for (const period1 of periods1) {
    for (const period2 of periods2) {
      const startMinutes = Math.max(period1.start, period2.start);
      const endMinutes = Math.min(period1.end, period2.end);
      if (startMinutes < endMinutes) {
        intersectingPeriods.push(
          makeTimeRange(minutesFromMidnightToTimeRecord(startMinutes), minutesFromMidnightToTimeRecord(endMinutes)),
        );
      }
    }
  }
  return intersectingPeriods;
}

export class WeeklyPatternRuleFunction implements RuleFunction {
  private readonly timezone: string;
  private readonly daysOfTheWeek: Set<DayOfTheWeek>;
  private readonly ruleStartDate: LocalisedDateTime | null = null;
  private readonly ruleEndDate: LocalisedDateTime | null = null;
  private readonly isTimeRangeAllDay: boolean = false;
  private readonly minuteRanges: TimeRangeInMinutes[] = [];
  private readonly effectivePeriod: OptionalEffectivePeriod;

  constructor(schedule: Schedule, rule: WeeklyPatternRule) {
    this.timezone = schedule.metadata.timezone;
    this.daysOfTheWeek = new Set();
    for (const day of rule.weekly_pattern.days) {
      this.daysOfTheWeek.add(day);
    }
    this.effectivePeriod = rule.weekly_pattern.effective_period;
    if (rule.weekly_pattern.effective_period.start) {
      this.ruleStartDate = dateTimeRecordToLocalisedDateTime(rule.weekly_pattern.effective_period.start, this.timezone);
    }
    if (rule.weekly_pattern.effective_period.end) {
      this.ruleEndDate = dateTimeRecordToLocalisedDateTime(rule.weekly_pattern.effective_period.end, this.timezone);
    }
    const processedRanges = _processTimeRanges(rule.weekly_pattern.time_ranges);
    if (processedRanges.isTimeRangeAllDay) {
      this.isTimeRangeAllDay = true;
    }
    this.minuteRanges.push(...processedRanges.minuteRanges);
  }

  appliesAt(datetime: DateTimeRecord): boolean {
    const queryDate = dateTimeRecordToLocalisedDateTime(datetime, this.timezone);
    if (this.ruleStartDate && queryDate < this.ruleStartDate) {
      return false;
    }
    if (this.ruleEndDate && queryDate >= this.ruleEndDate) {
      return false;
    }
    if (!this.daysOfTheWeek.has(dateTimeToDayOfWeek(queryDate))) {
      return false;
    }
    if (this.isTimeRangeAllDay) {
      return true;
    }
    const queryMinutes = dateTimeRecordToMinutesFromMidnight(datetime);
    for (const range of this.minuteRanges) {
      if (queryMinutes >= range.start && queryMinutes < range.end) {
        return true;
      }
    }
    return false;
  }

  periodsAppliedOn(date: DateRecord): PeriodsAppliedOnDate {
    const localisedDate = dateRecordToLocalisedDate(date, this.timezone);
    const dayOfWeek = dateTimeToDayOfWeek(localisedDate);
    if (!this.daysOfTheWeek.has(dayOfWeek)) {
      return { date, periods: [] };
    }

    const effectivePeriod: RequiredEffectivePeriod = {
      start: this.effectivePeriod.start ? this.effectivePeriod.start : dateRecordToStartOfDay(date),
      end: this.effectivePeriod.end ? this.effectivePeriod.end : dateRecordToEndOfDay(date),
    };
    const effectivePeriodResult = effectivePeriodsAppliedOn({
      date,
      timezone: this.timezone,
      effectivePeriod,
      precisionInMinutes: 1,
    });
    if (effectivePeriodResult.periods.length === 0) {
      return effectivePeriodResult;
    }

    const dayPeriods: TimeRangeInMinutes[] =
      this.isTimeRangeAllDay ? [{ start: 0, end: MINUTES_FROM_MIDNIGHT_END_OF_DAY }] : this.minuteRanges;

    const intersection = _periodIntersection(
      effectivePeriodResult.periods.map((period) => ({
        start: timeRecordToMinutesFromMidnight(period.start),
        end: timeRecordToMinutesFromMidnight(period.end),
      })),
      dayPeriods,
    );
    return { date, periods: intersection };
  }
}

export interface RotatingPatternCycleOffset {
  start: DateRecord;
  offset: number;
}

interface _RotatingPatternFunctionInterval {
  days: number;
  isOffDay: boolean;
  isTimeRangeAllDay: boolean;
  minuteRanges: TimeRangeInMinutes[];
}

export class RotatingPatternRuleFunction implements RuleFunction {
  private readonly timezone: string;
  private readonly ruleStartDate: LocalisedDateTime | null = null;
  private readonly ruleEndDate: LocalisedDateTime | null = null;
  private readonly patternStartDate: LocalisedDate;
  private readonly patternDuration: number;
  private readonly intervals: _RotatingPatternFunctionInterval[] = [];
  private readonly effectivePeriod: OptionalEffectivePeriod;
  private readonly precisionInMinutes: number;

  constructor(schedule: Schedule, rule: RotatingPatternRule) {
    this.timezone = schedule.metadata.timezone;
    this.precisionInMinutes = schedule.metadata.expected_time_precision_in_minutes;
    this.effectivePeriod = rule.rotating_pattern.effective_period;
    if (rule.rotating_pattern.effective_period.start) {
      this.ruleStartDate = dateTimeRecordToLocalisedDateTime(
        rule.rotating_pattern.effective_period.start,
        this.timezone,
      );
    }
    if (rule.rotating_pattern.effective_period.end) {
      this.ruleEndDate = dateTimeRecordToLocalisedDateTime(rule.rotating_pattern.effective_period.end, this.timezone);
    }
    this.patternStartDate = dateRecordToLocalisedDate(rule.rotating_pattern.start, this.timezone);
    this.patternDuration = 0;
    for (const interval of rule.rotating_pattern.pattern) {
      this.patternDuration += interval.days;
    }
    for (const interval of rule.rotating_pattern.pattern) {
      if ("time_ranges" in interval) {
        const processedRanges = _processTimeRanges(interval.time_ranges);
        this.intervals.push({
          days: interval.days,
          isOffDay: false,
          isTimeRangeAllDay: processedRanges.isTimeRangeAllDay,
          minuteRanges: processedRanges.minuteRanges,
        });
      } else if ("is_off_day" in interval) {
        this.intervals.push({
          days: interval.days,
          isOffDay: interval.is_off_day,
          isTimeRangeAllDay: false,
          minuteRanges: [],
        });
      } else {
        throw new RuleFunctionError(`Unhandled interval structure ${JSON.stringify(interval)}`);
      }
    }
  }

  /**
   * Given a point in time, indicates its offset from the pattern's cycles
   */
  cycleOffsetAt(date: DateRecord): RotatingPatternCycleOffset {
    const queryDate = dateRecordToLocalisedDate(date, this.timezone);
    const daysSincePatternStart = queryDate.diff(this.patternStartDate, "days").days;
    const startOffset = daysSincePatternStart % this.patternDuration;
    let cycleDate: LocalisedDate;
    let cycleOffset: number;
    if (startOffset > 0) {
      cycleDate = queryDate.minus({ days: startOffset });
      cycleOffset = startOffset;
    } else if (startOffset === 0) {
      cycleDate = queryDate;
      cycleOffset = 0;
    } else {
      cycleOffset = this.patternDuration + startOffset;
      cycleDate = queryDate.minus({ days: cycleOffset });
    }
    const cycleStart = makeDateRecord(cycleDate.year, cycleDate.month, cycleDate.day);
    return { start: cycleStart, offset: cycleOffset };
  }

  _getIntervalFor(dt: DateTimeRecord) {
    const cycleOffset = this.cycleOffsetAt(dateTimeRecordToDateRecord(dt));
    let offset = cycleOffset.offset;
    for (const interval of this.intervals) {
      offset -= interval.days;
      if (offset < 0) {
        return interval;
      }
    }
    throw new RuleFunctionError(`Failed to find matching interval for ${JSON.stringify(dt)}`);
  }

  appliesAt(dt: DateTimeRecord): boolean {
    const queryDate = dateTimeRecordToLocalisedDateTime(dt, this.timezone);
    if (this.ruleStartDate && queryDate < this.ruleStartDate) {
      return false;
    }
    if (this.ruleEndDate && queryDate >= this.ruleEndDate) {
      return false;
    }
    const interval = this._getIntervalFor(dt);
    if (interval.isOffDay) {
      return false;
    }
    if (interval.isTimeRangeAllDay) {
      return true;
    }
    const queryMinutes = dateTimeRecordToMinutesFromMidnight(dt);
    for (const range of interval.minuteRanges) {
      if (queryMinutes >= range.start && queryMinutes < range.end) {
        return true;
      }
    }
    return false;
  }

  periodsAppliedOn(date: DateRecord): PeriodsAppliedOnDate {
    const interval = this._getIntervalFor(dateRecordToDateTimeRecord(date));
    if (interval.isOffDay) {
      return { date, periods: [] };
    }
    const intervalPeriods: TimeRangeInMinutes[] =
      interval.isTimeRangeAllDay ? [{ start: 0, end: MINUTES_FROM_MIDNIGHT_END_OF_DAY }] : interval.minuteRanges;

    const effectivePeriod: RequiredEffectivePeriod = {
      start: this.effectivePeriod.start ? this.effectivePeriod.start : dateRecordToStartOfDay(date),
      end: this.effectivePeriod.end ? this.effectivePeriod.end : dateRecordToEndOfDay(date),
    };
    const effectivePeriodResult = effectivePeriodsAppliedOn({
      date,
      timezone: this.timezone,
      effectivePeriod,
      precisionInMinutes: this.precisionInMinutes,
    });
    if (effectivePeriodResult.periods.length === 0) {
      return effectivePeriodResult;
    }

    const intersection = _periodIntersection(
      effectivePeriodResult.periods.map((period) => ({
        start: timeRecordToMinutesFromMidnight(period.start),
        end: timeRecordToMinutesFromMidnight(period.end),
      })),
      intervalPeriods,
    );
    return { date, periods: intersection };
  }
}

export class AbsoluteRangeRuleFunction implements RuleFunction {
  private readonly timezone: string;
  private readonly precisionInMinutes: number;
  private readonly ruleStartDate: LocalisedDateTime;
  private readonly ruleEndDate: LocalisedDateTime | null = null;
  private readonly effectivePeriod: AbsoluteRangeEffectivePeriod;

  constructor(schedule: Schedule, rule: AbsoluteRangeRule) {
    this.timezone = schedule.metadata.timezone;
    this.precisionInMinutes = schedule.metadata.expected_time_precision_in_minutes;
    this.effectivePeriod = rule.absolute_range.effective_period;
    this.ruleStartDate = dateTimeRecordToLocalisedDateTime(rule.absolute_range.effective_period.start, this.timezone);
    if (rule.absolute_range.effective_period.end) {
      this.ruleEndDate = dateTimeRecordToLocalisedDateTime(rule.absolute_range.effective_period.end, this.timezone);
    }
  }

  appliesAt(datetime: DateTimeRecord): boolean {
    const queryDate = dateTimeRecordToLocalisedDateTime(datetime, this.timezone);
    if (queryDate < this.ruleStartDate) {
      return false;
    }
    if (this.ruleEndDate && queryDate >= this.ruleEndDate) {
      return false;
    }
    return true;
  }

  periodsAppliedOn(date: DateRecord): PeriodsAppliedOnDate {
    const effectivePeriod: RequiredEffectivePeriod = {
      start: this.effectivePeriod.start,
      end: this.effectivePeriod.end ? this.effectivePeriod.end : dateRecordToEndOfDay(date),
    };
    return effectivePeriodsAppliedOn({
      date,
      timezone: this.timezone,
      precisionInMinutes: this.precisionInMinutes,
      effectivePeriod,
    });
  }
}

export function effectivePeriodsAppliedOn({
  date,
  timezone,
  effectivePeriod,
  precisionInMinutes,
}: {
  date: DateRecord;
  timezone: string;
  effectivePeriod: RequiredEffectivePeriod;
  precisionInMinutes: number;
}): PeriodsAppliedOnDate {
  const effectiveStart = dateTimeRecordToLocalisedDateTime(effectivePeriod.start, timezone);
  const effectiveEnd = dateTimeRecordToLocalisedDateTime(effectivePeriod.end, timezone);
  const startOfDay = dateTimeRecordToLocalisedDateTime(dateRecordToStartOfDay(date), timezone);
  const endOfDay = dateTimeRecordToLocalisedDateTime(dateRecordToEndOfDay(date), timezone);
  // Date does not intersect with effective period
  if (effectiveStart >= endOfDay || effectiveEnd <= startOfDay) {
    return { date, periods: [] };
  }
  const effectiveEndIsSameDay = startOfDay.hasSame(effectiveEnd, "day");
  // Exclusive end of 00:00 on the same day should only apply up to this day, but not include it
  if (effectiveEndIsSameDay && effectiveEnd.hour === 0 && effectiveEnd.minute === 0) {
    return { date, periods: [] };
  }
  // Effective period with duration less than expected precision has no effect as the exclusive end will prevent it applying
  const diffInMinutes = Math.abs(effectiveEnd.diff(effectiveStart, "minutes").minutes);
  if (diffInMinutes < precisionInMinutes) {
    return { date, periods: [] };
  }
  const effectiveStartIsSameDay = startOfDay.hasSame(effectiveStart, "day");
  if (effectiveStartIsSameDay && effectiveEndIsSameDay) {
    // Effective period is wholly contained within the day
    return {
      date,
      periods: [
        makeTimeRange(
          makeTimeRecord(effectiveStart.hour, effectiveStart.minute),
          makeTimeRecord(effectiveEnd.hour, effectiveEnd.minute),
        ),
      ],
    };
  }
  if (effectiveStartIsSameDay) {
    return {
      date,
      periods: [makeTimeRange(makeTimeRecord(effectiveStart.hour, effectiveStart.minute), makeTimeRecord(24, 0))],
    };
  }
  if (effectiveEndIsSameDay) {
    return {
      date,
      periods: [makeTimeRange(makeTimeRecord(0, 0), makeTimeRecord(effectiveEnd.hour, effectiveEnd.minute))],
    };
  }
  // Day is wholly contained within the effective period
  return { date, periods: [makeTimeRange(makeTimeRecord(0, 0), makeTimeRecord(24, 0))] };
}

/**
 * Create a RuleFunction for a given rule.
 *
 * @template TFeatureName - The type of feature names
 * @template TFeatureState - The type of feature states
 */
export function makeRuleFunction<TFeatureName extends string = string, TFeatureState extends string = string>(
  schedule: Schedule<TFeatureName, TFeatureState>,
  rule: Rule<TFeatureName, TFeatureState>,
): RuleFunction {
  // Cast to base types for implementation to avoid conditional type complexity
  const sched = schedule as Schedule;
  const r = rule as Rule;

  switch (r.kind) {
    case "weekly_pattern":
      return new WeeklyPatternRuleFunction(sched, r);
    case "rotating_pattern":
      return new RotatingPatternRuleFunction(sched, r);
    case "absolute_range":
      return new AbsoluteRangeRuleFunction(sched, r);
    default:
      throw new RuleFunctionError(`Unknown kind in rule ${JSON.stringify(r)}`);
  }
}
